import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { EnvSyncService } from '../../services/env-sync/env-sync.service';
import { SchemaComparison, SchemaPushPlan } from '../../services/env-sync/types';
import { UtilsService } from '../../services/utils/utils.service';
import { RecordComparisonItem, SchemaComparisonItem } from './types';

type Tab = {
  id: string;
  title: string;
};

@Component({
    selector: 'app-env-sync',
    templateUrl: './env-sync.component.html',
    styleUrls: ['./env-sync.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EnvSyncComponent implements OnInit {
  protected isLoading = false;
  protected tabs: Tab[] = [];
  protected activeTab?: Tab;
  protected schemas: SchemaComparisonItem[] = [];

  constructor(
    private readonly envSyncService: EnvSyncService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly utils: UtilsService,
  ) {}

  async ngOnInit(): Promise<void> {
    const res = await this.envSyncService.listServices();

    this.tabs = res.data.map((serviceName) => ({
      id: serviceName,
      title: this.utils.capitalize(serviceName),
    }));

    if (this.tabs.length === 0) {
      return;
    }

    const fallbackServiceName = this.tabs[0].id;

    // Handle initial load
    const initialServiceName = this.route.snapshot.queryParamMap.get('serviceName');
    this.resolveActiveTabFromRoute(initialServiceName, fallbackServiceName);

    // Handle navigation events
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
      const serviceName = this.route.snapshot.queryParamMap.get('serviceName');
      this.resolveActiveTabFromRoute(serviceName, fallbackServiceName);
    });
  }

  private resolveActiveTabFromRoute(serviceName: string | null, fallbackServiceName: string): void {
    const validTab = this.tabs.find((tab) => tab.id === serviceName);
    if (validTab) {
      this.activeTab = validTab;
      this.loadSchemasForActiveTab();
    } else {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          serviceName: fallbackServiceName,
        },
        queryParamsHandling: 'merge',
      });
    }
  }

  onTabSelected(tab: Tab): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        serviceName: tab.id,
      },
      queryParamsHandling: 'merge',
    });
  }

  // Handles initial load and manual refresh
  async loadSchemasForActiveTab(): Promise<void> {
    if (!this.activeTab) {
      return;
    }
    this.schemas = [];
    this.isLoading = true;
    try {
      const res = await this.envSyncService.compareSchemas(this.activeTab.id);
      this.schemas = this.mapSchemaComparisonData(res.data);
      this.isLoading = false;
    } catch (e) {
      console.error('Error fetching schema comparison:', e);
      this.isLoading = false;
    }
  }

  private mapSchemaComparisonData(raw: SchemaComparison[]): SchemaComparisonItem[] {
    return raw.map((schema): SchemaComparisonItem => {
      const sortedRecords = [...schema.records].sort((a, b) => {
        return a.label?.localeCompare(b.label, undefined, { sensitivity: 'base' });
      });
      return {
        schemaName: schema.schemaName,
        records: sortedRecords.map(
          (record): RecordComparisonItem => ({
            ...record,
            isExpanded: false,
            isSelected: false,
          }),
        ),
        customFilters: schema.customFilters,
        isExpanded: false,
      };
    });
  }

  hasSelectedRecords(): boolean {
    return this.schemas.some((schema) => schema.records.some((record) => record.isSelected));
  }

  isPushEnabled(): boolean {
    if (!this.hasSelectedRecords()) {
      return false;
    }

    // TEMPORARY RESTRICTION: For now only allow 'rewards' schema to be pushed
    // TODO: Remove this restriction once other schemas are supported
    const schemasWithSelections = this.schemas.filter((schema) =>
      schema.records.some((record) => record.isSelected),
    );
    return schemasWithSelections.every((schema) => schema.schemaName === 'rewards');
  }

  async pushSelectedChanges(): Promise<void> {
    if (!this.activeTab) {
      console.error('No active tab selected');
      return;
    }

    // Collect all selected records from all schemas
    const plans: SchemaPushPlan[] = [];

    for (const schema of this.schemas) {
      const selectedRecords = schema.records.filter((record) => record.isSelected);

      if (selectedRecords.length > 0) {
        const plan: SchemaPushPlan = {
          schemaName: schema.schemaName,
          records: selectedRecords.map((record) => ({
            sourceRecordId: record.qa?.id!, // We assume qa record always exists if selected
            targetRecordId: record.prod?.id,
          })),
        };
        plans.push(plan);
      }
    }

    if (plans.length === 0) {
      console.warn('No records selected for push');
      return;
    }

    this.isLoading = true;
    try {
      await this.envSyncService.pushChanges(this.activeTab.id, plans);
      console.log('Successfully pushed changes to production');

      // Refresh the data to see updated state
      await this.loadSchemasForActiveTab();
    } catch (error) {
      console.error('Error pushing changes to production:', error);
    } finally {
      this.isLoading = false;
    }
  }
}
